import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(":", ".")
      .replace(/\s+/g, " ")
      .trim();

    return `${datePart} ${timePart}`;
  } catch {
    return String(value);
  }
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "-";
  if (startDate && endDate) return `${formatDateOnly(startDate)} to ${formatDateOnly(endDate)}`;
  return formatDateOnly(startDate) || formatDateOnly(endDate) || "-";
};

const toText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

export function TaskListPdfDocument({
  components,
  logoUrl,
  companyInfo,
  filters,
  report,
  selectedColumns,
  generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["task", "ticket", "assignee", "priority", "status", "activity", "actions"];
  const columnWidth = `${100 / activeColumns.length}%`;

  const styles = StyleSheet.create({
    page: {
      paddingTop: 22,
      paddingBottom: 24,
      paddingHorizontal: 24,
      backgroundColor: "#f8fafc",
      fontSize: 9,
      color: "#0f172a",
    },
    headerBand: {
      backgroundColor: "#0f172a",
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 16,
      marginBottom: 9,
    },
    headerTopRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 4,
    },
    brandWrap: {
      flexDirection: "row",
      alignItems: "center",
      maxWidth: "62%",
    },
    logo: {
      width: 44,
      height: 44,
      objectFit: "contain",
      marginRight: 10,
      backgroundColor: "#ffffff",
      borderRadius: 10,
      padding: 4,
    },
    brandEyebrow: {
      fontSize: 8,
      color: "#94a3b8",
      letterSpacing: 0.8,
      textTransform: "uppercase",
      marginBottom: 2,
    },
    companyName: {
      fontSize: 15,
      fontWeight: 700,
      color: "#ffffff",
    },
    headerContactWrap: {
      maxWidth: "38%",
      alignItems: "flex-end",
      backgroundColor: "#111f36",
      borderWidth: 1,
      borderColor: "#24344e",
      borderRadius: 8,
      paddingVertical: 6,
      paddingHorizontal: 8,
    },
    headerContactText: {
      fontSize: 8.1,
      color: "#e2e8f0",
      lineHeight: 1.4,
      textAlign: "right",
      marginBottom: 1,
    },
    headerContactAddress: {
      fontSize: 7.8,
      color: "#cbd5e1",
      lineHeight: 1.3,
      textAlign: "right",
      marginBottom: 3,
    },
    headerContactLabel: {
      fontSize: 7.4,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 1,
    },
    cardGrid: {
      flexDirection: "row",
      marginBottom: 10,
    },
    card: {
      flexGrow: 1,
      flexBasis: 0,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 10,
      padding: 9,
      marginRight: 8,
    },
    cardLast: {
      marginRight: 0,
    },
    cardLabel: {
      fontSize: 7.5,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      marginBottom: 3,
    },
    cardValue: {
      fontSize: 13,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 1,
    },
    cardHelper: {
      fontSize: 8.2,
      color: "#475569",
      lineHeight: 1.35,
    },
    section: {
      marginBottom: 8,
    },
    sectionTitle: {
      fontSize: 11.5,
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: 4,
    },
    filterSummaryBox: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    filterSummaryText: {
      fontSize: 8.6,
      color: "#0f172a",
      lineHeight: 1.5,
    },
    table: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#0f172a",
      borderBottomWidth: 1,
      borderBottomColor: "#1e293b",
      paddingVertical: 7,
    },
    tableHeaderText: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#e2e8f0",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      paddingHorizontal: 6,
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#e2e8f0",
      paddingVertical: 6,
    },
    tableRowAlt: {
      backgroundColor: "#f8fafc",
    },
    tableRowLast: {
      borderBottomWidth: 0,
    },
    cellWrap: {
      paddingHorizontal: 6,
    },
    cellText: {
      fontSize: 7.8,
      color: "#0f172a",
      lineHeight: 1.35,
    },
    cellTextStrong: {
      fontSize: 7.8,
      fontWeight: 700,
      color: "#0f172a",
      lineHeight: 1.35,
    },
    cellHint: {
      fontSize: 7.1,
      color: "#475569",
      lineHeight: 1.35,
      marginTop: 2,
    },
    emptyState: {
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderColor: "#cbd5e1",
      borderRadius: 8,
      paddingVertical: 14,
      paddingHorizontal: 14,
    },
    emptyStateText: {
      fontSize: 9,
      color: "#475569",
      lineHeight: 1.5,
      textAlign: "center",
    },
    footer: {
      marginTop: 6,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: "#cbd5e1",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    footerText: {
      fontSize: 8,
      color: "#64748b",
    },
  });

  const statusCounts = Array.isArray(report?.statusCounts) ? report.statusCounts : [];
  const countsByStatus = statusCounts.reduce((result, item) => {
    result[item?.status] = Number(item?.count || 0);
    return result;
  }, {});

  const summaryCards = [
    {
      label: "Total Tasks",
      value: String(report?.totalTasks || 0),
      helper: `${report?.items?.length || 0} row${report?.items?.length === 1 ? "" : "s"} included in this export`,
    },
    {
      label: "Completed",
      value: String(countsByStatus.Completed || 0),
      helper: `In Progress ${countsByStatus["In Progress"] || 0} | Waiting ${countsByStatus.Waiting || 0}`,
    },
    {
      label: "Pending / Cancelled",
      value: `${countsByStatus.Pending || 0} / ${countsByStatus.Cancelled || 0}`,
      helper: `Status filter: ${toText(filters?.status, "All Statuses")}`,
    },
  ];

  const filterSummaryParts = [
    `Date: ${formatDateRange(filters?.startDate, filters?.endDate)}`,
    filters?.requestedUserName ? `User: ${filters.requestedUserName}` : null,
    filters?.requestedUserId ? `User ID: ${filters.requestedUserId}` : null,
    filters?.status && filters.status !== "All Statuses" ? `Status: ${filters.status}` : null,
    `Generated: ${formatDateTime(generatedAt)}`,
  ].filter(Boolean);

  const columns = [
    {
      key: "task",
      header: "Task",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{toText(item.title, "Untitled task")}</Text>
          <Text style={styles.cellHint}>{toText(item.description, "No description")}</Text>
          <Text style={styles.cellHint}>
            Created by {toText(item.createdByName, "Unknown")} | Due {formatDateOnly(item.dueDate)}
          </Text>
        </View>
      ),
    },
    {
      key: "ticket",
      header: "Ticket",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{toText(item.ticketNumber, "Manual task")}</Text>
          <Text style={styles.cellHint}>{toText(item.ticketSubject, "No linked ticket subject")}</Text>
        </View>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{toText(item.assignedEmployeeName, "Unassigned")}</Text>
          <Text style={styles.cellHint}>{toText(item.assignedEmployeeEmail || item.assignedUserId, "No email")}</Text>
        </View>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{toText(item.priority, "Low")}</Text>
        </View>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{toText(item.status, "Pending")}</Text>
          <Text style={styles.cellHint}>Progress {Number(item.progressPercent || 0)}%</Text>
        </View>
      ),
    },
    {
      key: "activity",
      header: "Last Activity",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{formatDateTime(item.lastActivityAt || item.updatedAt)}</Text>
          <Text style={styles.cellHint}>
            {toText(item.lastActionLabel, "Task Updated")} by {toText(item.lastActorName, "Unknown")}
          </Text>
          <Text style={styles.cellHint}>{toText(item.lastNote, "No note")}</Text>
        </View>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <View style={[styles.cellWrap, { width: columnWidth }]}>
          <Text style={styles.cellTextStrong}>{item.ticketId ? "Ticket / Task Desk" : "Task Desk"}</Text>
          <Text style={styles.cellHint}>
            {item.ticketId ? `Ticket ${toText(item.ticketNumber, "Open Ticket")}` : "Manual task"}
          </Text>
        </View>
      ),
    },
  ].filter((column) => activeColumns.includes(column.key));

  return (
    <Document
      title="Task Report"
      author="BanglaFire CRM"
      subject="Task report"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.headerTopRow}>
            <View style={styles.brandWrap}>
              {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
              <View>
                <Text style={styles.brandEyebrow}>Task Report</Text>
                <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
              </View>
            </View>
            <View style={styles.headerContactWrap}>
              <Text style={styles.headerContactAddress}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
              <Text style={styles.headerContactLabel}>Mobile</Text>
              <Text style={styles.headerContactText}>(+880) 9639222999, 1709927934</Text>
              <Text style={styles.headerContactLabel}>Email</Text>
              <Text style={styles.headerContactText}>md@banglafire.com, support@banglafire.com</Text>
            </View>
          </View>
        </View>

        <View style={styles.cardGrid}>
          {summaryCards.map((card, index) => (
            <View key={card.label} style={[styles.card, index === summaryCards.length - 1 ? styles.cardLast : null]}>
              <Text style={styles.cardLabel}>{card.label}</Text>
              <Text style={styles.cardValue}>{card.value}</Text>
              <Text style={styles.cardHelper}>{card.helper}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Report Filters</Text>
          <View style={styles.filterSummaryBox}>
            <Text style={styles.filterSummaryText}>{filterSummaryParts.join(" | ")}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Task Report Rows</Text>

          {report?.items?.length ? (
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                {columns.map((column) => (
                  <Text key={column.key} style={[styles.tableHeaderText, { width: columnWidth }]}>
                    {column.header}
                  </Text>
                ))}
              </View>
              {report.items.map((item, index) => (
                <View
                  key={item.taskId || `${item.title}-${index}`}
                  style={[
                    styles.tableRow,
                    index % 2 === 1 ? styles.tableRowAlt : null,
                    index === report.items.length - 1 ? styles.tableRowLast : null,
                  ]}
                  wrap={false}
                >
                  {columns.map((column) => (
                    <React.Fragment key={column.key}>
                      {column.render(item)}
                    </React.Fragment>
                  ))}
                </View>
              ))}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No tasks matched the selected report filters. Adjust the filters and export again if needed.
              </Text>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM Professional Report</Text>
        </View>
      </Page>
    </Document>
  );
}
